import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useToaster } from "core/hooks/useToaster";
import { useUserTokenInfo } from "core/hooks/useUserTokenInfo";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePaymentRequestId } from "./usePaymentRequestId";

export function usePaymentRequestComments(requestId?: string | string[]) {
  const { errorToast, successToast } = useToaster();
  const { t } = useTranslation("PaymentRequest");
  const user = useUserTokenInfo();
  const centrixFetch = useCentrixFetch();
  const paymentRequestId = usePaymentRequestId(requestId);

  const { data, mutate, isError, isLoading } = useCentrixApi({
    path: "/api/app/payment-request-comment",
    parameters: {
      query: {
        PaymentRequestId: paymentRequestId,
      },
    },
    fetchAll: true,
  });

  const { items, totalCount } = useMemo(() => {
    if (!data) {
      return { items: [], totalCount: 0 };
    }
    return { items: data.items || [], totalCount: data.totalCount || 0 };
  }, [data]);

  async function deleteComment(id: string) {
    const deleteRes = await centrixFetch({
      method: "delete",
      path: `/api/app/payment-request-comment/{id}`,
      parameters: { path: { id } },
    });
    if (!deleteRes.ok) {
      errorToast(
        t("Next:PaymentRequest:usePaymentRequestComments.deleteCommentError")
      );
      return;
    }
    mutate((currentData) => {
      if (!currentData) {
        return { items: [], totalCount: 0 };
      }
      const filteredItems =
        currentData?.items?.filter(({ id: commentId }) => id !== commentId) ||
        [];
      return { items: filteredItems, totalCount: filteredItems.length };
    });
    successToast(
      t("Next:PaymentRequest:usePaymentRequestComments.deleteCommentSuccess")
    );
  }
  const updateCommentErrorMessage = t(
    "Next:PaymentRequest:usePaymentRequestComments.updateCommentError"
  );
  const updateCommentSuccessMessage = t(
    "Next:PaymentRequest:usePaymentRequestComments.updateCommentSuccess"
  );

  async function updateComment(id: string, comment: string) {
    const currentComment = data?.items?.find(
      ({ id: commentId }) => commentId === id
    );
    if (!currentComment) {
      errorToast(updateCommentErrorMessage);
      return;
    }

    const updatedCommentRes = await centrixFetch({
      method: "put",
      path: "/api/app/payment-request-comment/{id}",
      parameters: { path: { id } },
      body: {
        comment,
        paymentRequestId: currentComment.paymentRequestId,
        source: currentComment.source,
        sendEmail: false,
      },
    });
    if (!updatedCommentRes.ok) {
      errorToast(updateCommentErrorMessage);
      return;
    }

    const updatedComment = await updatedCommentRes.json();

    mutate((currentData) => {
      if (!currentData) {
        return { items: [updatedComment], totalCount: 1 };
      }
      const updatedItems =
        currentData?.items?.map((currentItem) =>
          currentItem.id === id
            ? {
                ...updatedComment,
                creator: {
                  name: user?.name,
                  surname: user?.surname,
                  userName: user?.userName,
                },
              }
            : currentItem
        ) || [];

      return {
        items: updatedItems,
        totalCount: updatedItems.length,
      };
    });

    successToast(updateCommentSuccessMessage);
  }

  return {
    items,
    totalCount,
    mutate,
    isError,
    isLoading,
    deleteComment,
    updateComment,
  };
}
